/**
 * Block 5.2 (2026-05-27): Closing snapshot display.
 *
 * 4 conditional sections — кожна optional залежно від period:
 * - expense_closing/revenue_closing: monthly
 * - quarterly_snapshot: Q1/Q2/Q3 ends only
 * - annual_closing: December only (Q4)
 * - used_profit_closing: грудень, якщо на рахунку 443 був залишок (ч.1134)
 *
 * Subconto breakdown — collapsible <details>.
 */
import type { ClosingSnapshot } from '@/api/month_close_runs';

const fmt = (v: string | number | undefined | null) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export default function ClosingSnapshotCard({ snapshot }: { snapshot: ClosingSnapshot | null }) {
  if (!snapshot) return null;
  const { expense_closing, revenue_closing, quarterly_snapshot, annual_closing } = snapshot;
  const used_profit_closing = snapshot.used_profit_closing ?? null;
  if (!expense_closing && !revenue_closing && !quarterly_snapshot && !annual_closing
      && !used_profit_closing) {
    return null;
  }
  const used = Number(used_profit_closing?.used_profit ?? 0);

  return (
    <div
      data-testid="closing-snapshot-card"
      className="card"
      style={{ marginTop: '1rem' }}
    >
      <h3 style={{ marginTop: 0 }}>Закриття місяця</h3>

      {revenue_closing && (
        <div data-testid="revenue-closing-section" style={{ marginBottom: '0.75rem' }}>
          <strong style={{ color: 'var(--badge-green-fg)' }}>Закриття доходів (70x → 791)</strong>
          <p style={{ margin: '0.25rem 0' }}>
            Всього: <strong>{fmt(revenue_closing.total_amount)} грн</strong>
            {' · '}
            проводок: {revenue_closing.entries_count ?? 0}
          </p>
          {revenue_closing.accounts && revenue_closing.accounts.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                Розбивка за рахунками ({revenue_closing.accounts.length})
              </summary>
              <table style={{ width: '100%', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                <tbody>
                  {revenue_closing.accounts.map((a) => (
                    <tr key={a.account_code}>
                      <td>{a.account_code}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(a.amount)} грн</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}

      {expense_closing && (
        <div data-testid="expense-closing-section" style={{ marginBottom: '0.75rem' }}>
          <strong style={{ color: 'var(--badge-amber-fg)' }}>Закриття витрат (8x/9x → 791)</strong>
          <p style={{ margin: '0.25rem 0' }}>
            Всього: <strong>{fmt(expense_closing.total_amount)} грн</strong>
            {' · '}
            проводок: {expense_closing.entries_count ?? 0}
          </p>
          {expense_closing.subconto_breakdown && expense_closing.subconto_breakdown.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                Розбивка за рахунками і проектами ({expense_closing.subconto_breakdown.length})
              </summary>
              <table style={{ width: '100%', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                    <th>Рахунок</th>
                    <th>Стаття витрат</th>
                    <th>Проект</th>
                    <th style={{ textAlign: 'right' }}>Сума</th>
                  </tr>
                </thead>
                <tbody>
                  {expense_closing.subconto_breakdown.map((row, idx) => (
                    <tr key={`${row.account_code}-${idx}`}>
                      <td>{row.account_code}</td>
                      <td style={{ color: row.cost_item_id ? 'var(--text)' : 'var(--muted)' }}>
                        {row.cost_item_id ?? '—'}
                      </td>
                      <td style={{ color: row.project_id ? 'var(--text)' : 'var(--muted)' }}>
                        {row.project_id ?? '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(row.amount)} грн</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}

      {quarterly_snapshot && (
        <div data-testid="quarterly-snapshot-section" style={{ marginBottom: '0.75rem' }}>
          <strong style={{ color: 'var(--badge-blue-fg)' }}>
            Квартальний підсумок Q{quarterly_snapshot.quarter} (YTD)
          </strong>
          <p style={{ margin: '0.25rem 0' }}>
            Доходи з початку року: <strong>{fmt(quarterly_snapshot.ytd_revenue)} грн</strong>
          </p>
          <p style={{ margin: '0.25rem 0' }}>
            Витрати з початку року: <strong>{fmt(quarterly_snapshot.ytd_expenses)} грн</strong>
          </p>
          <p style={{ margin: '0.25rem 0', fontSize: '1.05rem' }}>
            Фін.результат YTD:{' '}
            <strong
              style={{
                color: Number(quarterly_snapshot.ytd_financial_result) >= 0
                  ? 'var(--badge-green-fg)' : 'var(--badge-red-fg)',
              }}
            >
              {fmt(quarterly_snapshot.ytd_financial_result)} грн
            </strong>
          </p>
        </div>
      )}

      {annual_closing && (
        <div
          data-testid="annual-closing-section"
          style={{
            padding: '0.75rem',
            background: Number(annual_closing.financial_result) >= 0 ? 'var(--badge-green-bg)' : 'var(--badge-red-bg)',
            border: `1px solid ${
              Number(annual_closing.financial_result) >= 0 ? 'var(--badge-green-bd)' : 'var(--badge-red-bd)'
            }`,
            borderRadius: 6,
          }}
        >
          <strong>Річне закриття 791 → {annual_closing.transfer_account ?? '—'}</strong>
          <p style={{ margin: '0.25rem 0' }}>
            Фін.результат за рік: <strong>{fmt(annual_closing.financial_result)} грн</strong>
          </p>
          {annual_closing.transfer_account && (
            <p style={{ margin: '0.25rem 0' }}>
              Переведено на рахунок <strong>{annual_closing.transfer_account}</strong>:{' '}
              <strong>{fmt(annual_closing.transfer_amount)} грн</strong>
              {' '}
              ({annual_closing.transfer_account === '441' ? 'прибуток' : 'збиток'})
            </p>
          )}
          {annual_closing.entries_count === 0 && (
            <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Фін.результат нульовий — проводки не створено.
            </p>
          )}
        </div>
      )}

      {used_profit_closing && (
        <div
          data-testid="used-profit-closing-section"
          style={{
            marginTop: '0.75rem', padding: '0.75rem', background: 'var(--bg-soft)',
            border: '1px solid var(--border)', borderRadius: 6,
          }}
        >
          <strong>Річне закриття рахунку 443</strong>
          {used >= 0 ? (
            <>
              <p style={{ margin: '0.25rem 0' }}>
                Використано за рік: <strong>{fmt(used)} грн</strong>
              </p>
              {Number(used_profit_closing.from_profit ?? 0) > 0 && (
                <p style={{ margin: '0.25rem 0' }}>
                  За рахунок нерозподіленого прибутку (рахунок <strong>441</strong>):{' '}
                  <strong>{fmt(used_profit_closing.from_profit)} грн</strong>
                </p>
              )}
              {Number(used_profit_closing.to_loss ?? 0) > 0 && (
                <p style={{ margin: '0.25rem 0' }}>
                  Понад прибуток — на непокритий збиток (рахунок <strong>442</strong>):{' '}
                  <strong>{fmt(used_profit_closing.to_loss)} грн</strong>
                </p>
              )}
            </>
          ) : (
            <>
              <p style={{ margin: '0.25rem 0' }}>
                Внесено більше, ніж використано: <strong>{fmt(-used)} грн</strong>
              </p>
              {Number(used_profit_closing.covers_loss ?? 0) > 0 && (
                <p style={{ margin: '0.25rem 0' }}>
                  Зменшено непокритий збиток (рахунок <strong>442</strong>):{' '}
                  <strong>{fmt(used_profit_closing.covers_loss)} грн</strong>
                </p>
              )}
              {Number(used_profit_closing.to_profit ?? 0) > 0 && (
                <p style={{ margin: '0.25rem 0' }}>
                  Додано до нерозподіленого прибутку (рахунок <strong>441</strong>):{' '}
                  <strong>{fmt(used_profit_closing.to_profit)} грн</strong>
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
